import {Badge, Flag, getInitials} from 'platform/components';
import {Show} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {cn} from 'platform/shared';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CALENDAR_CONSTANTS} from '../constants/calendarConstants';
import type {RentalCalendarScale} from '../types/RentalCalendarScale';
import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';
import type {CollisionLayout} from '../utils/detectEventCollisions';
import {getEventBarLayout} from '../utils/getEventBarLayout';
import {getEventCollisionLayout} from '../utils/getEventCollisionLayout';
import {BLOCK_COLOR, getRentalStateAppearance} from '../utils/getRentalStateAppearance';

interface RentalCalendarEventBarProps extends RequiredTestIdProps {
  event: RentalResourceCalendarEvent;
  eventIndex: number;
  scale: RentalCalendarScale;
  trackWidth: number;
  collisionLayout: CollisionLayout;
  blockReasonLabels: Map<string, string>;
  isHovered: boolean;
  referenceRef: (element: HTMLElement | null) => void;
  onEventClick?: (event: RentalResourceCalendarEvent) => void;
  onEventMouseEnter: (event: RentalResourceCalendarEvent) => void;
  onEventMouseLeave: () => void;
}

export function RentalCalendarEventBar(props: RentalCalendarEventBarProps) {
  const formatDateTime = useDateTimeFormatter();

  const barLayout = getEventBarLayout({
    start: props.event.start,
    end: props.event.end,
    scale: props.scale,
    trackWidth: props.trackWidth,
  });

  const appearance = getRentalStateAppearance(props.event.rentalOrder?.state);
  const collisionInfo = getEventCollisionLayout(props.event, props.collisionLayout);

  const isBlock = props.event.type === 'block';
  const barColor = isBlock ? BLOCK_COLOR : appearance.cssColor;

  const barHeight = Math.floor(CALENDAR_CONSTANTS.ROW_HEIGHT * BAR_HEIGHT_RATIO);
  const barTop =
    collisionInfo.rowIndex * CALENDAR_CONSTANTS.ROW_HEIGHT +
    Math.floor(CALENDAR_CONSTANTS.ROW_HEIGHT * BAR_TOP_RATIO);

  const title = isBlock
    ? (props.blockReasonLabels.get(props.event.block?.reasonId ?? '') ??
      i18n.t('entity.rental.labels.vehicleBlock'))
    : (props.event.rentalOrder?.customerName ?? i18n.t('page.rentalOrder.labels.notPlannedYet'));

  const timeLabel = `${formatDateTime('dateTimeShort', props.event.start)} - ${formatDateTime('dateTimeShort', props.event.end)}`;

  return (
    <div
      ref={props.isHovered ? props.referenceRef : undefined}
      className={cn(
        'absolute flex cursor-pointer select-none items-center overflow-hidden rounded-small border-2 px-2 py-1 hover:shadow-elevation-2',
        barLayout.isClampedStart && 'rounded-l-none border-l-0',
        barLayout.isClampedEnd && 'rounded-r-none border-r-0'
      )}
      style={{
        top: barTop,
        left: barLayout.left,
        width: barLayout.width,
        height: barHeight,
        zIndex: props.eventIndex + 1,
        borderColor: barColor,
        backgroundColor: getSubtleBackground(barColor),
      }}
      data-testid={suffixTestId(`event-${props.event.id}`, props)}
      onClick={(mouseEvent) => {
        // The track behind the bar creates a rental on click — an event click must not reach it
        mouseEvent.stopPropagation();
        props.onEventClick?.(props.event);
      }}
      onMouseEnter={() => props.onEventMouseEnter(props.event)}
      onMouseLeave={props.onEventMouseLeave}
    >
      {barLayout.width < COMPACT_BAR_WIDTH ? (
        <div className="flex w-full items-center justify-center">
          <span className="truncate text-xs font-bold text-primary">
            {isBlock ? title : getInitials(title)}
          </span>
        </div>
      ) : (
        <div className="flex w-full flex-col items-center justify-center">
          <div className="flex max-w-full items-center gap-1">
            <span className="truncate text-xs font-bold text-primary">{title}</span>
            <Show when={!isBlock && barLayout.width >= FLAG_BAR_WIDTH}>
              <Flag size="small" label={appearance.label} colorScheme={appearance.colorScheme} />
            </Show>
            <Show when={!isBlock && barLayout.width >= BADGE_BAR_WIDTH}>
              <Badge
                colorScheme={appearance.colorScheme}
                size="small"
                variant="subtle"
                value={props.event.rentalOrder?.rentalLength ?? 1}
              />
            </Show>
          </div>
          <span className="truncate text-[0.625rem] text-primary">{timeLabel}</span>
        </div>
      )}
    </div>
  );
}

/** Subtle state tint, mixed into the surface rather than transparent so grid lines stay hidden */
const getSubtleBackground = (color: string) =>
  `color-mix(in srgb, ${color} 12%, var(--color-palettes-white-10-100))`;

const BAR_HEIGHT_RATIO = 0.8;
const BAR_TOP_RATIO = 0.1;

/** Below this pixel width the bar only shows customer initials, or a block's bare reason */
const COMPACT_BAR_WIDTH = 90;
/** From this pixel width the state flag fits in */
const FLAG_BAR_WIDTH = 200;
/** From this pixel width the rental length badge fits in */
const BADGE_BAR_WIDTH = 260;
